<script setup lang="ts">
	import type { RuleObject } from 'ant-design-vue/es/form'
	import type { ClassNameValue } from 'tailwind-merge'

	// ─── Types ───────────────────────────────────────────────────────────────────

	interface StrengthLevel {
		label: string
		color: string
		steps: number
	}

	interface Props {
		/** Label for the password a-form-item */
		label?: string
		/** name attribute forwarded to a-form-item */
		name?: string
		/** Placeholder for the password input */
		placeholder?: string
		/** Extra CSS class applied to the a-input-password element */
		inputClass?: ClassNameValue
		/** Validation rules forwarded to a-form-item */
		rules?: RuleObject | RuleObject[]
		/** Show the real-time strength indicator */
		showIndicator?: boolean
		/** Show a confirm-password field */
		showConfirm?: boolean
		/** Label for the confirm field */
		confirmLabel?: string
		/** name attribute for the confirm a-form-item */
		confirmName?: string
		/** Placeholder for the confirm field */
		confirmPlaceholder?: string
		/** Extra CSS class applied to confirm a-input-password element */
		confirmInputClass?: ClassNameValue
	}

	// ─── Props & Model ────────────────────────────────────────────────────────────

	const props = withDefaults(defineProps<Props>(), {
		label: 'Password',
		name: 'password',
		placeholder: 'Enter password',
		showIndicator: true,
		showConfirm: false,
		confirmLabel: 'Confirm Password',
		confirmName: 'confirm_password',
		confirmPlaceholder: 'Re-enter password'
	})

	/** Only the password value is exposed via v-model — confirm is internal state only */
	const password = defineModel<string>({ default: '' })

	// ─── Internal confirm state ───────────────────────────────────────────────────

	const confirmValue = ref('')
	const confirmTouched = ref(false)

	// ─── Requirement checks ───────────────────────────────────────────────────────

	const requirements = computed(() => [
		{ key: 'length', label: 'At least 8 characters', met: password.value.length >= 8 },
		{ key: 'uppercase', label: 'One uppercase letter', met: /[A-Z]/.test(password.value) },
		{ key: 'number', label: 'One number', met: /[0-9]/.test(password.value) },
		{ key: 'special', label: 'One special character', met: /[^A-Za-z0-9]/.test(password.value) }
	])

	const metCount = computed(() => requirements.value.filter(r => r.met).length)
	const isAllMet = computed(() => metCount.value === 4)

	// ─── Strength levels ──────────────────────────────────────────────────────────

	const strengthLevels: StrengthLevel[] = [
		{ label: 'Too weak', color: '#F04438', steps: 1 },
		{ label: 'Weak', color: '#F79009', steps: 2 },
		{ label: 'Good', color: '#12B76A', steps: 3 },
		{ label: 'Strong', color: '#039855', steps: 4 }
	]

	const currentStrength = computed<StrengthLevel | null>(() => {
		if (!password.value) return null
		const idx = Math.min(metCount.value - 1, 3)
		return idx >= 0 ? strengthLevels[idx]! : strengthLevels[0]!
	})

	function barColor(barIndex: number): string {
		if (!currentStrength.value || !password.value) return '#E4E7EC'
		return barIndex <= currentStrength.value.steps ? currentStrength.value.color : '#E4E7EC'
	}

	// ─── Confirm field validator ──────────────────────────────────────────────────

	const confirmValidator = async (_rule: RuleObject) => {
		if (!confirmValue.value || !confirmValue.value.trim()) {
			return Promise.reject(new Error('Please confirm your password'))
		}
		if (confirmValue.value !== password.value) {
			return Promise.reject(new Error('Passwords do not match'))
		}
		return Promise.resolve()
	}
</script>

<template>
	<div class="pw-field-wrap">
		<!-- Main password field -->
		<a-form-item :label :name :rules :colon="false" class="!mb-0">
			<a-input-password v-model:value="password" :placeholder :class="cn('custom-input', inputClass)" />
		</a-form-item>

		<!-- Strength indicator -->
		<Transition name="pw-slide">
			<div v-if="showIndicator && password.length > 0" class="pw-indicator">
				<!-- Strength bars -->
				<div class="pw-bars" aria-hidden="true">
					<span
						v-for="i in 4"
						:key="i"
						class="pw-bar"
						:class="{ 'pw-bar--pulse': isAllMet && i === 4 }"
						:style="{ backgroundColor: barColor(i) }"
					/>
				</div>

				<!-- Strength label -->
				<p v-if="currentStrength" class="pw-strength-label" :style="{ color: currentStrength.color }">
					{{ currentStrength.label }}
				</p>

				<!-- Requirements checklist -->
				<ul class="pw-requirements" role="list" aria-label="Password requirements">
					<li
						v-for="req in requirements"
						:key="req.key"
						class="pw-req-item"
						:class="{ 'pw-req-item--met': req.met }"
					>
						<span class="pw-req-dot" aria-hidden="true">
							<svg v-if="req.met" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none">
								<circle cx="8" cy="8" r="8" fill="#039855" />
								<path
									d="M4.5 8.25l2.25 2.25 4.75-5"
									stroke="#fff"
									stroke-width="1.5"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
							<svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="none">
								<circle cx="8" cy="8" r="7" stroke="#D0D5DD" stroke-width="1.5" />
							</svg>
						</span>
						<span class="pw-req-text">{{ req.label }}</span>
					</li>
				</ul>
			</div>
		</Transition>

		<!-- Confirm password field -->
		<Transition name="pw-slide">
			<a-form-item
				v-if="showConfirm"
				:label="confirmLabel"
				:name="confirmName"
				:colon="false"
				:rules="[{ validator: confirmValidator, trigger: ['change', 'blur'] }]"
				class="!mb-0 !mt-6"
			>
				<a-input-password
					v-model:value="confirmValue"
					:placeholder="confirmPlaceholder"
					:class="cn('custom-input', confirmInputClass)"
					@blur="confirmTouched = true"
				/>
			</a-form-item>
		</Transition>
	</div>
</template>

<style scoped>
	.pw-field-wrap {
		display: flex;
		flex-direction: column;
		gap: 0;
		width: 100%;
	}

	.pw-indicator {
		margin-top: 10px;
		display: flex;
		flex-direction: column;
		gap: 8px;
	}

	.pw-bars {
		display: flex;
		gap: 6px;
	}

	.pw-bar {
		flex: 1;
		height: 4px;
		border-radius: 999px;
		background-color: #e4e7ec;
		transition: background-color 0.3s ease;
	}

	.pw-bar--pulse {
		animation: pw-pulse 1s ease-in-out 1;
	}

	@keyframes pw-pulse {
		0% {
			transform: scaleX(1);
			opacity: 1;
		}
		40% {
			transform: scaleX(1.06);
			opacity: 0.8;
		}
		100% {
			transform: scaleX(1);
			opacity: 1;
		}
	}

	.pw-strength-label {
		font-size: 12px;
		font-weight: 600;
		line-height: 1;
		margin: 0;
		transition: color 0.25s ease;
	}

	.pw-requirements {
		list-style: none;
		padding: 0;
		margin: 0;
		display: flex;
		flex-direction: column;
		gap: 6px;
	}

	.pw-req-item {
		display: flex;
		align-items: center;
		gap: 8px;
	}

	.pw-req-dot {
		display: flex;
		align-items: center;
		flex-shrink: 0;
	}

	.pw-req-dot svg {
		width: 16px;
		height: 16px;
		transition: transform 0.2s ease;
	}

	.pw-req-item--met .pw-req-dot svg {
		transform: scale(1.08);
	}

	.pw-req-text {
		font-size: 12px;
		line-height: 1.4;
		color: #667085;
		transition: color 0.2s ease;
	}

	.pw-req-item--met .pw-req-text {
		color: #039855;
	}

	.pw-slide-enter-active,
	.pw-slide-leave-active {
		transition:
			opacity 0.22s ease,
			transform 0.22s ease;
	}

	.pw-slide-enter-from,
	.pw-slide-leave-to {
		opacity: 0;
		transform: translateY(-6px);
	}
</style>
